import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Heart, Printer, Mail } from "lucide-react";

const cards = [
  {
    id: "joker",
    symbol: "🃏",
    label: "Joker",
    answer: "NO",
    cardSub: "Not at this time.",
    meaning: "Not this. Not now.",
    body: "Trust the no. It is guidance, not punishment. The no is protecting your highest good in a way you cannot yet see.",
    color: "#B91C1C",
    border: "border-[#DC2626]/40",
  },
  {
    id: "spade",
    symbol: "♠",
    label: "Ace of Spades",
    answer: "WAIT",
    cardSub: "You need more information.",
    meaning: "You need more information.",
    body: "Go quiet. Ground. Ask again tomorrow. The answer will come — but not from the part of you that is afraid right now.",
    color: "#0F172A",
    border: "border-[#1E3A8A]/40",
  },
  {
    id: "heart",
    symbol: "♥",
    label: "Queen of Hearts",
    answer: "YES",
    cardSub: "",
    meaning: "Proceed — but start small.",
    body: "Do not make any BIG choices from trauma, fight, or flight. One gentle step. That is enough. That is HYGIENE.",
    color: "#25a1c2",
    border: "border-[#25a1c2]/40",
  },
];

const ritualSteps = [
  { num: "01", title: "Pull your three cards", text: "From a regular deck of playing cards, set aside: the Joker, the Ace of Spades, and the Queen of Hearts. (Or print mine below — paper works just as beautifully. Only rule: all three cards must be the same size.)" },
  { num: "02", title: "Ground", text: "Plant your feet. Soften your shoulders. Take one slow breath. Feel the earth under the floor, the floor under you, you — here, now." },
  { num: "03", title: "Hold the cards over your heart", text: "All three. Stacked. Pressed lightly to your chest. Let them warm." },
  { num: "04", title: "Ask", text: "Out loud or within: \"Holy Spirit, angels and guides — is this for my highest good?\" (Those are my words. Use whatever name for the Divine feels true to you — whatever grounds you, whatever connects you to your highest good.)" },
  { num: "05", title: "Shuffle, and listen", text: "Shuffle slowly. Listen for the stop. When you feel it, pull one card. Trust what it says. YES — start small. NO — respect it. WAIT — wait." },
];

export default function YesNoWait() {
  useEffect(() => {
    document.title = "Yes. No. Wait. — The HYGIENE of the Sacred Pause | Temple Franklin";
    const desc = document.querySelector('meta[name="description"]');
    const prev = desc?.getAttribute("content");
    if (desc) desc.setAttribute("content", "Yes. No. Wait. A free 3-card check-in ritual from Temple Franklin. Ground. Ask. Listen. The HYGIENE of the sacred pause — for women who no longer make big choices from fight or flight.");
    return () => { if (desc && prev) desc.setAttribute("content", prev); };
  }, []);

  const handlePrint = () => window.print();

  return (
    <div className="bg-[#efeae7] min-h-screen" data-testid="yes-no-wait-page">
      <header className="fixed top-0 w-full z-50 bg-white/90 backdrop-blur-xl border-b border-[#CCFBF1] print:hidden">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-[#25a1c2] hover:text-[#042F2E] text-sm font-medium inline-flex items-center gap-1.5" data-testid="home-link">
            <ArrowLeft className="w-4 h-4" /> Home
          </Link>
          <div className="flex items-center gap-5">
          <Link to="/build-your-own" className="text-[#25a1c2] hover:text-[#042F2E] text-sm font-medium" data-testid="ynw-nav-build-your-own">Build Your Own</Link>
          <button
            onClick={handlePrint}
            className="inline-flex items-center gap-2 bg-[#042F2E] hover:bg-[#068fa2] text-white text-xs font-bold rounded-full px-4 py-2"
            data-testid="print-button"
          >
            <Printer className="w-3.5 h-3.5" /> Print / Save as PDF
          </button>
          </div>
        </div>
      </header>

      <section className="pt-32 pb-12 px-6 print:pt-8">
        <div className="max-w-5xl mx-auto text-center">
          <p className="font-sans text-xs tracking-[0.3em] uppercase text-[#25a1c2] font-medium mb-5">A free gift from Temple Franklin</p>
          <h1 className="font-serif text-6xl md:text-8xl tracking-tighter text-[#042F2E] leading-[0.95] mb-5" data-testid="page-title">
            Yes. No.<br /><span className="italic text-[#25a1c2]">Wait.</span>
          </h1>
          <p className="font-serif text-xl md:text-2xl italic text-[#068fa2] max-w-2xl mx-auto leading-relaxed">
            The HYGIENE of the Sacred Pause
          </p>
          <p className="font-sans text-[#068fa2] max-w-2xl mx-auto mt-6 leading-relaxed text-base md:text-lg">
            Three cards. One question. Your highest good.
            <br />
            A check-in ritual for the woman who no longer makes big choices from fight or flight.
          </p>
        </div>
      </section>

      <section className="pb-16 px-6 print:hidden">
        <div className="max-w-3xl mx-auto bg-white rounded-3xl border border-[#CCFBF1] p-8 md:p-10">
          <p className="font-sans text-xs tracking-[0.2em] uppercase text-[#25a1c2] font-medium mb-3">A note from Temple</p>
          <div className="space-y-4 text-[#068fa2] leading-relaxed text-[17px]">
            <p>To this day, I am still unraveling those fight-or-flight choices I made before I knew how to pause.</p>
            <p>
              So I made this — for the woman who has said <strong>NO</strong>, the buck stops here, and now needs a way to check in with her own highest good before the next decision, the next yes, the next leap.
            </p>
            <p>
              It is deliberately humble: three cards from an ordinary deck. Paper works too. You do not need a $40 oracle deck or a psychic or a retreat. You need your own hand on your own heart, and the willingness to wait for the answer.
            </p>
            <p className="font-serif italic text-[#042F2E]">
              Do not make any BIG choices from trauma, fight, or flight. Start small. Start here.
            </p>
          </div>
        </div>
      </section>

      {/* WHEN TO USE THIS — explicit emergency use case */}
      <section className="pb-16 px-6 print:break-before-page">
        <div className="max-w-3xl mx-auto">
          <div className="bg-[#F0FDF4] border-2 border-[#25a1c2]/30 rounded-3xl p-8 md:p-10" data-testid="when-to-use-card">
            <p className="font-sans text-xs tracking-[0.3em] uppercase text-[#25a1c2] font-bold mb-3">When to use these cards</p>
            <h2 className="font-serif text-3xl md:text-4xl text-[#042F2E] leading-tight mb-4">
              When emotions are too heavy<br/>and you must decide <em>right now.</em>
            </h2>
            <div className="space-y-3 text-[#068fa2] leading-relaxed">
              <p>
                This is <strong>not</strong> the daily morning practice. That is Tapped-In — the preventive HYGIENE that comes before life gets heavy.
              </p>
              <p>
                <strong>This is different.</strong> This is for the moments when the storm is already here. A decision is on your lap. The text needs a response. The yes-or-no is waiting. And you cannot hear your own inner voice through the noise of overwhelm.
              </p>
              <p className="font-serif italic text-[#042F2E] text-lg">
                In those moments — call on your Divine Team. Pull a card. Surrender the answer to your highest good.
              </p>
              <p>
                Not every moment calls for this. But when one does — you'll know. And now you have a tool.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-16 px-6 print:break-before-page">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <p className="font-sans text-xs tracking-[0.2em] uppercase text-[#25a1c2] font-medium mb-2">The Ritual</p>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-[#042F2E]">
              Five steps. Sixty seconds.
            </h2>
          </div>

          <div className="space-y-5">
            {ritualSteps.map((s) => (
              <div
                key={s.num}
                className="bg-white rounded-2xl border border-[#CCFBF1] p-6 md:p-7 flex flex-col md:flex-row gap-5 md:gap-7"
                data-testid={`step-${s.num}`}
              >
                <span className="font-serif text-5xl md:text-6xl text-[#5EEAD4] leading-none flex-shrink-0">{s.num}</span>
                <div>
                  <h3 className="font-serif text-xl md:text-2xl text-[#042F2E] mb-2">{s.title}</h3>
                  <p className="text-[#068fa2] leading-relaxed">{s.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-16 px-6 print:break-before-page">
        <div className="max-w-3xl mx-auto">
          <div className="bg-gradient-to-br from-[#042F2E] via-[#068fa2] to-[#25a1c2] rounded-3xl p-8 md:p-12 text-white">
            <p className="font-sans text-xs tracking-[0.3em] uppercase text-[#5EEAD4] font-medium mb-4" data-testid="deeper-note-label">A deeper note — on the BIG choices</p>
            <div className="space-y-5 text-white/90 leading-relaxed text-[17px]">
              <p>
                When you ask this question with full <strong className="text-[#5EEAD4]">trusted surrender</strong> — real faith in your Divine guidance, real grounding, real presence —
              </p>
              <p className="font-serif italic text-white text-xl md:text-2xl">
                even a YES or a NO can leave space for BIG choices.
              </p>
              <p>
                Because when you are not in fight or flight, you can hear. When you can hear, you can trust. When you can trust, you can act.
              </p>
              <p>
                Either way — YES, NO, or WAIT — <strong className="text-[#5EEAD4]">you GET TO choose to listen.</strong> And you will learn from any of the choices.
              </p>
              <p className="pt-3 border-t border-white/15">
                This is the practice I come home to, always. Foster <strong className="text-[#5EEAD4]">the CC's</strong>:
              </p>
              <p className="font-serif text-3xl md:text-4xl text-[#5EEAD4] tracking-tight leading-tight">
                Compassionate Curiosity<br />
                <span className="text-white/80 text-xl md:text-2xl italic">— for yourself, and for others.</span>
              </p>
              <p className="font-serif italic text-white/70 pt-2">
                That is the HYGIENE.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-20 px-6 print:break-before-page">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10 print:mb-6">
            <p className="font-sans text-xs tracking-[0.2em] uppercase text-[#25a1c2] font-medium mb-2">The Cards</p>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-[#042F2E]">
              Print. Cut. Carry.
            </h2>
            <p className="font-sans text-[#068fa2] mt-3 max-w-xl mx-auto print:hidden">
              Each card is designed at standard playing-card size (2.5" × 3.5"). Print on card stock if you have it. Regular paper works beautifully too — and so do <strong className="text-[#042F2E]">blank-back business cards</strong>, if that is what you have on hand.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 print:gap-3">
            {cards.map((c) => (
              <div key={c.id} className="group" data-testid={`card-${c.id}`}>
                <div
                  className={`relative bg-white rounded-2xl overflow-hidden aspect-[2.5/3.5] flex flex-col p-6 border-[3px] ${c.border} shadow-2xl print:shadow-none print:border-2`}
                  style={{ color: c.color }}
                >
                  <div className="flex justify-between items-start">
                    <span className="font-serif text-2xl">{c.symbol}</span>
                    <span className="text-[10px] font-bold uppercase tracking-[0.2em]">{c.label}</span>
                  </div>
                  <div className="flex-1 flex items-center justify-center">
                    <span className="font-serif text-7xl md:text-8xl tracking-tight">{c.symbol}</span>
                  </div>
                  <div className="text-center">
                    <p className="font-serif text-4xl md:text-5xl tracking-tighter mb-1">{c.answer}</p>
                    {c.cardSub && (
                      <p className="font-sans italic text-sm opacity-70">{c.cardSub}</p>
                    )}
                  </div>
                  <div className="flex justify-between items-end mt-2">
                    <span className="text-[10px] font-bold uppercase tracking-[0.2em]">{c.label}</span>
                    <span className="font-serif text-2xl rotate-180">{c.symbol}</span>
                  </div>
                </div>

                <div className="mt-4 bg-white rounded-2xl border border-[#CCFBF1] p-5 print:border-dashed print:border-[#A7A29E]">
                  <p className="font-serif text-xs tracking-[0.2em] uppercase text-[#25a1c2] mb-1.5">If you pull {c.label}</p>
                  <p className="font-serif text-2xl text-[#042F2E] mb-1">{c.answer}</p>
                  <p className="text-[#068fa2] text-[15px] leading-relaxed italic mb-2">{c.meaning}</p>
                  <p className="text-[#068fa2] text-sm leading-relaxed">{c.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-20 px-6 print:hidden">
        <div className="max-w-3xl mx-auto bg-gradient-to-br from-[#042F2E] via-[#068fa2] to-[#25a1c2] rounded-3xl p-10 md:p-12 text-center text-white">
          <Heart className="w-8 h-8 text-[#5EEAD4] mx-auto mb-4" />
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight mb-4">
            One letter a week. From my kitchen table to yours.
          </h2>
          <p className="text-white/80 leading-relaxed max-w-xl mx-auto mb-7">
            If these cards land, you'll love the free 52-week journey home. Real tools, for real women, in the messy middle. No upsells. No urgency. Just a porch light — on.
          </p>
          <a
            href="/workbook?from=yes-no-wait"
            className="inline-flex items-center gap-2 bg-[#5EEAD4] hover:bg-white text-[#042F2E] font-bold rounded-full px-8 py-4 text-sm tracking-wider uppercase transition-all"
            data-testid="join-journey-cta"
          >
            <Mail className="w-4 h-4" /> Join the free 52-week journey
          </a>
          <p className="font-serif italic text-[#5EEAD4] mt-8 text-lg">
            There is a candle in the window of your own soul.
          </p>
          <p className="font-serif italic text-white/70 mt-1">— Welcome home.</p>
        </div>
      </section>


      <style>{`
        @media print {
          body { background: white !important; }
          @page { size: letter; margin: 0.5in; }
        }
      `}</style>
    </div>
  );
}
